// ============================================================
// Full path:
//   D:\Thyroid Consultation Software\ThyroConsult Frontend\thyroconsult-frontend\src\utils\fitPlaceholder.js
//
// Shrink-to-fit placeholder text (4 Oct 2026).
//
// A placeholder (the light grey hint inside an empty box) is always one
// line, so a long hint such as the password rule gets cut off — worse on a
// phone. With this, the GREY HINT shrinks just enough to fit the box in
// full; what the person TYPES stays at the normal size, because only the
// ::placeholder font size changes.
//
//   useAutoFitPlaceholders();   // call once at the top of a page component
//
// It looks after every <input> / <textarea> with a placeholder on the
// page, including boxes that appear later (next step of a form), hints that
// change (mobile hint changes with the country), and screen-size changes.
// The hint never goes below MIN_PX; a hint that already fits is untouched.
// ============================================================

import { useEffect } from 'react';

const MIN_PX = 9;
const VAR = '--tc-ph-size';

let styleInjected = false;
function injectStyle() {
  if (styleInjected || typeof document === 'undefined') return;
  styleInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    input[data-tc-fit-ph]::placeholder,
    textarea[data-tc-fit-ph]::placeholder { font-size: var(${VAR}, inherit); }
  `;
  document.head.appendChild(style);
}

let canvas = null;
function textWidth(text, font) {
  if (!canvas) canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.font = font;
  return ctx.measureText(text).width;
}

export function fitPlaceholder(el) {
  if (!el || !el.placeholder) return;
  const cs = window.getComputedStyle(el);
  const avail = el.clientWidth - parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0) - 2;
  if (avail <= 0) return;                     // hidden right now — fitted when it appears
  const base = parseFloat(cs.fontSize) || 14;
  const width = textWidth(el.placeholder, `${cs.fontStyle} ${cs.fontWeight} ${base}px ${cs.fontFamily}`);
  el.setAttribute('data-tc-fit-ph', '');
  if (width <= avail) {
    el.style.removeProperty(VAR);
  } else {
    const size = Math.max(MIN_PX, Math.floor((base * avail / width) * 10) / 10);
    el.style.setProperty(VAR, `${size}px`);
  }
}

function fitAll() {
  document.querySelectorAll('input[placeholder], textarea[placeholder]').forEach(fitPlaceholder);
}

export function useAutoFitPlaceholders() {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    injectStyle();

    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => { frame = 0; fitAll(); });
    };

    schedule();
    window.addEventListener('resize', schedule);
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['placeholder'] });
    // Web fonts arriving late change text widths
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule).catch(() => {});

    return () => {
      window.removeEventListener('resize', schedule);
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);
}
